import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

import { menusDeleteMenu, menusGetMenuDetails } from "~/api-refs";
import { AlertDialogLoading } from "~/components/shared/alert-dialog-loading";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

export const Route = createFileRoute("/_authenticated/menus/$menuId/delete")({
  component: DeleteMenuModal,
});

function DeleteMenuModal() {
  const { menuId } = Route.useParams();
  const navigate = useNavigate();

  // Fetch menu details for display
  const { data: menuDetails, isPending } = useQuery(
    convexQuery(menusGetMenuDetails, {
      menuId: toConvexTableId("menus", menuId),
    })
  );

  const deleteMenuMutation = useMutation({
    mutationFn: (data: { menuId: Id<"menus"> }) => {
      return convexClient.mutation(menusDeleteMenu, data);
    },
  });

  const handleClose = () => {
    void navigate({ to: `/menus/${menuId}/categories` });
  };

  const handleDelete = async () => {
    if (deleteMenuMutation.isPending) {
      return;
    }
    try {
      const menuName = menuDetails?.menuName ?? "Menu";
      // Navigate BEFORE deleting to avoid querying deleted menu
      await navigate({ to: "/menus" });
      toast.success(`${menuName} has been deleted`);
      // Track menu deletion in PostHog
      ProductEvents.menuDeleted(menuId);
      // Small delay to ensure navigation starts before deletion
      await new Promise((resolve) => setTimeout(resolve, 50));
      await deleteMenuMutation.mutateAsync({
        menuId: toConvexTableId("menus", menuId),
      });
    } catch (error) {
      logger.exception("menu_deletion_failed", error);
      toast.error("Failed to delete menu");
    }
  };

  if (isPending) {
    return (
      <AlertDialogLoading
        message="Loading menu details..."
        onOpenChange={handleClose}
      />
    );
  }

  return (
    <AlertDialog onOpenChange={handleClose} open>
      <AlertDialogContent>
        <AlertDialogHeader className="items-center space-y-0">
          <AlertDialogTitle>Delete Menu</AlertDialogTitle>
          <AlertDialogDescription className="body">
            Are you sure you want to delete{" "}
            {menuDetails?.menuName ?? "this menu"}?
          </AlertDialogDescription>
        </AlertDialogHeader>

        {menuDetails && (
          <div className="bg-muted flex items-center justify-start gap-3 rounded-2xl px-5 py-4">
            <div className="flex w-full flex-col items-start justify-center gap-1">
              <div className="items-cente flex w-full justify-between gap-1 self-stretch">
                <div>{menuDetails.menuName}</div>
                <div className="caption flex items-center gap-1">
                  <div className="bg-category-bg text-category rounded-full px-2 py-1">
                    {menuDetails.categoryCount} categories
                  </div>
                  <div className="bg-muted text-muted-foreground rounded-full px-2 py-1">
                    {menuDetails.itemCount} items
                  </div>
                </div>
              </div>
              <div className="body-sm text-muted-foreground leading-tight">
                {menuDetails.locationNames}
              </div>
            </div>
          </div>
        )}

        <p className="body-sm text-foreground">
          This will permanently delete this menu. Shared categories and items in
          other menus will not be affected.
        </p>

        <AlertDialogFooter>
          <AlertDialogCancel variant="ghost">Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={deleteMenuMutation.isPending}
            onClick={handleDelete}
            variant="destructive"
          >
            <Trash2 className="mr-2 size-4" />
            Delete Menu
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
